import { useId, useState, type ReactElement } from 'react';
import type { BoardFaceList, ListCardSortBody } from '@molt/shared';
import { Button, Dialog, IconButton, Notice } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { X } from 'lucide-react';

export interface ListOperationsDialogProps {
  readonly open: boolean;
  readonly list: BoardFaceList;
  readonly lists: readonly BoardFaceList[];
  readonly busy?: boolean | undefined;
  readonly onCancel: () => void;
  readonly onSort: (mode: ListCardSortBody['mode']) => void;
  readonly onArchiveAll: () => void;
  readonly onMoveAll: (destinationListId: string) => void;
}

export function ListOperationsDialog({
  open,
  list,
  lists,
  busy = false,
  onCancel,
  onSort,
  onArchiveAll,
  onMoveAll,
}: ListOperationsDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const destinationId = useId();
  const [destinationListId, setDestinationListId] = useState('');

  if (!open) return null;

  const count = list.openCardCount;
  /* ⚠ The count is passed TWICE at every one of the four plural sites — into
     `values` for the `{count}` slot and as the arm selector. `interfaceMessage`
     defaults `count` to 0, so dropping the third argument renders
     `Archive 1 cards` with nothing red anywhere. */
  const countValues = { count };
  const otherLists = lists.filter((entry) => entry.id !== list.id);
  const effectiveDestinationId =
    destinationListId === '' ? (otherLists[0]?.id ?? '') : destinationListId;

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('copyMove.listOps.title', { list: list.title })}
      description={t('copyMove.listOps.description', countValues, count)}
      closeControl={
        <IconButton label={t('copyMove.listOps.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <Button size="sm" variant="secondary" onClick={onCancel}>
          {t('copyMove.done')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Notice
          tone="info"
          title={t('copyMove.listOps.noticeTitle')}
          body={t('copyMove.listOps.noticeBody')}
        />

        <section
          className="flex flex-col gap-2"
          aria-label={t('copyMove.listOps.sortRegion')}
        >
          <h3 className="font-display text-sm font-semibold text-ink">
            {t('copyMove.listOps.sortHeading')}
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onSort('title')}
            >
              {t('copyMove.listOps.sort.title')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onSort('due_date')}
            >
              {t('copyMove.listOps.sort.dueDate')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onSort('created_newest')}
            >
              {t('copyMove.listOps.sort.newest')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onSort('created_oldest')}
            >
              {t('copyMove.listOps.sort.oldest')}
            </Button>
          </div>
        </section>

        {/* ⚠ `Bulk actions` a SECOND time inside `copyMove.*`, on purpose: this
            is a `<section aria-label>`, where `copyMove.bulk.title` is a
            `Dialog` accessible name in another file. */}
        <section
          className="flex flex-col gap-2"
          aria-label={t('copyMove.listOps.bulkRegion')}
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || count === 0}
            disabledReason={count === 0 ? t('copyMove.listOps.noOpenCards') : undefined}
            onClick={onArchiveAll}
          >
            {t('copyMove.listOps.archiveAll', countValues, count)}
          </Button>

          <label htmlFor={destinationId} className="text-sm font-medium text-ink">
            {t('copyMove.destinationListLabel')}
          </label>
          <select
            id={destinationId}
            value={effectiveDestinationId}
            disabled={busy || count === 0 || otherLists.length === 0}
            onChange={(event) => setDestinationListId(event.currentTarget.value)}
            className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
          >
            {otherLists.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.title}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || count === 0 || effectiveDestinationId === ''}
            disabledReason={count === 0 ? t('copyMove.listOps.noOpenCards') : undefined}
            onClick={() => onMoveAll(effectiveDestinationId)}
          >
            {t('copyMove.listOps.moveAll', countValues, count)}
          </Button>
        </section>
      </div>
    </Dialog>
  );
}
